import { useState } from "react";
import { MainCard, MainCardTitle } from "src/components/main-card-type.tsx";
import type { SalarioResultsType } from "../types.d";
import { RANKING_ESTADOS } from "../data/dadosPNAD";
import { MAGISTRADOS_POR_ESTADO } from "../data/constants";
import { PercentilRing } from "./graficos/PercentilRing";
import { ComparacaoSalarios } from "./graficos/ComparacaoSalarios";
import { ProfissoesGrafico } from "./graficos/ProfissoesGrafico";
import { GraficoInvestimento } from "./graficos/GraficoInvestimento";
import { 
  CORES_SITE, 
  TIPOGRAFIA, 
  ESPACAMENTO, 
  CONTAINERS,
  TRANSICOES 
} from "../data/visualConstants";

const obterSiglaEstado = (nomeEstado: string): string => {
  const match = nomeEstado.match(/^([A-Z]{2})/);
  return match ? match[1] : nomeEstado.substring(0, 2).toUpperCase();
};

const obterSalarioMagistrado = (nomeEstado: string): number => {
  const sigla = obterSiglaEstado(nomeEstado);
  return MAGISTRADOS_POR_ESTADO[sigla as keyof typeof MAGISTRADOS_POR_ESTADO] || 37563;
};

const RankingEstadual = ({ results }: { results: SalarioResultsType }) => {
  const [hoveredState, setHoveredState] = useState<string | null>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  
  const siglaEstado = obterSiglaEstado(results.nomeEstado);
  
  // Calcular valor máximo para as barras de progresso
  const maxValor = Math.max(...RANKING_ESTADOS.map(e => e.valor));
  
  // Criar visualização de todos os estados
  const todosEstados = RANKING_ESTADOS.map(item => ({
    ...item,
    isUsuario: item.uf === siglaEstado,
    larguraBarra: (item.valor / maxValor) * 100
  }));

  const handleMouseEnter = (estado: any, event: React.MouseEvent) => {
    setHoveredState(estado.uf);
    setMousePosition({
      x: event.clientX,
      y: event.clientY
    });
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    setMousePosition({
      x: event.clientX,
      y: event.clientY
    });
  };

  const handleMouseLeave = () => {
    setHoveredState(null);
  };

  const getTooltipInfo = (uf: string) => {
    return RANKING_ESTADOS.find(e => e.uf === uf);
  };

  return (
    <div className={`${ESPACAMENTO.ENTRE_SECOES} ${CONTAINERS.SECAO_FUNDO_NEUTRO} ${ESPACAMENTO.CARD_PADDING} relative`}>
      <p className="text-base font-semibold text-gray-700 text-center mb-4">
        <strong>{results.nomeEstado}</strong> está em{" "}
        <span className="font-bold" style={{ color: CORES_SITE.AMARELO_DESTAQUE }}>
          {results.posicaoRanking}
        </span>
        {" "}no ranking de rendimento médio do país. Seu salário está acima da média de{" "}
        <span className="font-bold">{27 - results.rankingEstado} estados</span>.
      </p>
      
      {/* Lista vertical compacta com barras horizontais */}
      <div className="space-y-1 max-h-96 overflow-y-auto">
        {todosEstados.map((estado) => (
          <div
            key={estado.uf}
            className={`
              flex items-center gap-3 p-2 rounded cursor-pointer
              ${estado.isUsuario 
                ? 'bg-orange-50 border-l-4 border-orange-500' 
                : 'hover:bg-gray-50'
              }
              ${TRANSICOES.PADRAO}
            `}
            onMouseEnter={(e) => handleMouseEnter(estado, e)}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          >
            {/* Posição */}
            <span className={`${TIPOGRAFIA.LABEL_PEQUENO} font-semibold text-gray-500 w-8 text-center`}>
              {estado.posicao}º
            </span>
            
            {/* Sigla do estado */}
            <span 
              className={`${TIPOGRAFIA.CORPO} font-semibold w-12`}
              style={{ 
                color: estado.isUsuario ? CORES_SITE.AMARELO_DESTAQUE : CORES_SITE.CINZA_ESCURO
              }}
            >
              {estado.uf}
            </span>
            
            {/* Barra de progresso */}
            <div className="flex-1">
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className={`h-2 rounded-full ${TRANSICOES.SUAVE}`}
                  style={{ 
                    width: `${estado.larguraBarra}%`,
                    background: estado.isUsuario 
                      ? CORES_SITE.AMARELO_DESTAQUE
                      : `linear-gradient(to right, ${CORES_SITE.VERMELHO_PRIMARY}, ${CORES_SITE.AMARELO_DESTAQUE})`
                  }}
                />
              </div>
            </div>
            
            {/* Valor */}
            <span className={`${TIPOGRAFIA.LABEL_PEQUENO} font-semibold text-gray-700 w-24 text-right`}>
              R$ {estado.valor.toLocaleString('pt-BR')}
            </span>
          </div>
        ))}
      </div>
      
      {/* Tooltip customizado */}
      {hoveredState && (
        <div 
          className="fixed z-50 bg-gray-900 text-white p-3 rounded-lg shadow-xl pointer-events-none border border-gray-700 max-w-xs"
          style={{
            left: mousePosition.x + 10,
            top: mousePosition.y - 10,
            transform: typeof window !== 'undefined' && mousePosition.x > window.innerWidth / 2 ? 'translateX(-100%)' : 'translateX(0)'
          }}
        >
          {(() => {
            const info = getTooltipInfo(hoveredState);
            return info ? (
              <div className="text-sm">
                <div className="font-bold text-orange-300 mb-1">
                  {info.posicao}º lugar - {info.nome}
                </div>
                <div className="text-gray-300">
                  Renda média: <span className="text-green-400 font-semibold">R$ {info.valor.toLocaleString('pt-BR')}</span>
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  {hoveredState === siglaEstado ? '🏠 Seu estado' : 'Estado brasileiro'}
                </div>
              </div>
            ) : null;
          })()}
        </div>
      )}
      
      <div className="flex justify-center mt-4 pt-4 border-t border-gray-200">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded" style={{ backgroundColor: CORES_SITE.AMARELO_DESTAQUE }}></div>
          <span className={TIPOGRAFIA.LEGENDA}>Seu estado em destaque</span>
        </div>
      </div>
    </div>
  );
};

const ComparacaoNacionalEstadual = ({ results }: { results: SalarioResultsType }) => {
  return (
    <div className={ESPACAMENTO.ENTRE_SECOES}>
      {/* Subtítulo explicativo - Aumentado para text-lg */}
      <p className="text-lg font-bold text-gray-700 text-center mb-6">
        Compare sua posição em relação a todos os brasileiros e aos moradores do seu estado
      </p>

      <div className="grid sm:grid-cols-2 gap-6 mb-4 grid-cols-1">
        <div className="flex flex-col items-center">
          <PercentilRing
            percentual={results.percentilNacional}
            label=""
            sublabel=""
            cor={CORES_SITE.VERMELHO_PRIMARY}
            tamanho={140}
          />
          {/* Texto abaixo do gráfico - Aumentado para text-base */}
          <p className="text-base text-gray-700 text-center mt-3">
            Você ganha mais que <span className="font-bold" style={{ color: CORES_SITE.VERMELHO_PRIMARY }}>{results.percentilNacional}%</span> dos brasileiros
          </p>
        </div>

        <div className="flex flex-col items-center">
          <PercentilRing
            percentual={results.percentilEstadual}
            label=""
            sublabel=""
            cor={CORES_SITE.AMARELO_DESTAQUE}
            tamanho={140}
          />
          {/* Texto abaixo do gráfico - Aumentado para text-base */}
          <p className="text-base text-gray-700 text-center mt-3">
            Você ganha mais que <span className="font-bold" style={{ color: CORES_SITE.AMARELO_DESTAQUE }}>{results.percentilEstadual}%</span> dos moradores do seu estado, {results.nomeEstado}
          </p>
        </div>
      </div>
      
      <p className="text-xs text-gray-500 text-center mt-6">
        Dados baseados na PNAD 2025 (IBGE) - Rendimento habitual do trabalho principal
      </p>
    </div>
  );
};

const CargosPublicos = ({ results }: { results: SalarioResultsType }) => {
  const salarioUsuario = Math.round((results.comparacaoDeputado / 100) * 46366);
  const salarioMagistradoEstado = obterSalarioMagistrado(results.nomeEstado);
  
  return (
    <div className={ESPACAMENTO.ENTRE_SECOES}>
      {/* Título da seção */}
      <h2 className={TIPOGRAFIA.TITULO_SECAO}>
        Comparação com cargos públicos
      </h2>
      
      {/* Subtítulo explicativo */}
      <p className={TIPOGRAFIA.SUBTITULO_EXPLICATIVO}>
        Veja como seu salário se compara com os altos cargos do serviço público brasileiro
      </p>
      
      <ComparacaoSalarios
        salarioUsuario={salarioUsuario}
        comparacaoDeputado={results.comparacaoDeputado}
        comparacaoMagistrado={results.comparacaoMagistrado}
        salarioMagistradoEstado={salarioMagistradoEstado} 
      />
      
      {/* Nota explicativa sobre valores estaduais*/}
      <p className="text-xs text-gray-500 text-center mt-4">
        Valores: Deputado Federal R$ 46.366 (nacional) e Magistrado Estadual R$ {salarioMagistradoEstado.toLocaleString('pt-BR')} no {results.nomeEstado} (CNJ 2024)
      </p>
    </div>
  );
};

const ProfissoesEstado = ({ results }: { results: SalarioResultsType }) => {
  return (
    <div className={ESPACAMENTO.ENTRE_SECOES}>
      {/* Título da seção */}
      <h2 className={TIPOGRAFIA.TITULO_SECAO}>
        Comparação com profissões locais
      </h2>
      
      {/* Subtítulo explicativo */}
      <p className={TIPOGRAFIA.SUBTITULO_EXPLICATIVO}>
        No estado do(a) <strong>{results.nomeEstado}</strong>, considerando jornada de 40 horas semanais, você ganha:
      </p>
      
      <ProfissoesGrafico 
        profissoes={results.profissoes}
        nomeEstado={results.nomeEstado}
      />
      
      {/* Texto de apoio */}
      <p className={TIPOGRAFIA.TEXTO_APOIO}>
        Dados RAIS 2024 - Salários médios ajustados para 40 horas semanais
      </p>
    </div>
  );
};

const SimuladorInvestimento = ({ results }: { results: SalarioResultsType }) => {
  const salarioUsuario = Math.round((results.comparacaoDeputado / 100) * 46366);
  const salarioMedioBrasil = 3500; // Aproximado conforme PNAD
  const ehSalarioMedio = salarioUsuario >= salarioMedioBrasil * 0.8 && salarioUsuario <= salarioMedioBrasil * 1.5;
  
  return (
    <div className={ESPACAMENTO.ENTRE_SECOES}>
      {/* Título da seção */}
      <h2 className={TIPOGRAFIA.TITULO_SECAO}>
        Potencial de crescimento patrimonial
      </h2>
      
      {/* Subtítulo explicativo com justificativa */}
      <p className={TIPOGRAFIA.SUBTITULO_EXPLICATIVO}>
        Transforme sua posição salarial em perspectiva de futuro: veja como 25% do seu salário 
        pode crescer ao longo do tempo com investimentos
      </p>
      
      <GraficoInvestimento simulacao={results.simulacaoInvestimento} />
      
      <p className="text-xs text-gray-500 text-center mt-6">
        Simulação educativa com 8% a.a. | {ehSalarioMedio 
          ? 'Mesmo com salário médio, é possível construir patrimônio significativo'
          : 'Com disciplina e investimentos, é possível construir patrimônio significativo'
        }
      </p>
    </div>
  );
};

const Metodologia = () => {
  return (
    <div className={`${CONTAINERS.SECAO_FUNDO_NEUTRO} ${ESPACAMENTO.CARD_PADDING} mt-6`}>
      <h4 className={`${TIPOGRAFIA.SUBTITULO} mb-3`} style={{ color: CORES_SITE.AMARELO_DESTAQUE }}>
        Sobre a metodologia
      </h4>
      <div className={`${TIPOGRAFIA.CORPO_SECUNDARIO} space-y-2`}>
        <p>
          <strong>Dados salariais:</strong> Baseados na PNAD (Pesquisa Nacional por Amostra de Domicílios) 
          contínua do 2º trimestre de 2025, realizada pelo IBGE. Considera apenas rendimento do trabalho principal.
        </p>
        <p>
          <strong>Profissões:</strong> Dados da RAIS 2024, ajustados para jornada de 40 horas semanais.
        </p>
        <p>
          <strong>Cargos públicos:</strong> Deputado Federal (R$ 46.366,19) e Magistrado Estadual mediano por estado (CNJ 2024), variando de R$ 35.586 (RS) a R$ 40.917 (TO).
        </p>
        <p>
          <strong>Simulação de investimento:</strong> Cálculo educativo com rentabilidade de 8% ao ano.
        </p>
      </div>
    </div>
  );
};

export const SalarioResponseCard = ({ results }: { results: SalarioResultsType }) => {
  return (
    <MainCard className="mt-4">
      <MainCardTitle>Seu salário diante da realidade brasileira</MainCardTitle>
      
      {/* Seção 1: Comparação Nacional e Estadual */}
      <ComparacaoNacionalEstadual results={results} />
      
      {/* Seção 2: Ranking Estadual */}
      <RankingEstadual results={results} />
      
      {/* Seção 3: Cargos Públicos */}
      <CargosPublicos results={results} />
      
      {/* Seção 4: Profissões do Estado */}
      <ProfissoesEstado results={results} />
      
      {/* Seção 5: Simulador de Investimento */}
      <SimuladorInvestimento results={results} />
      
      {/* Metodologia */}
      <Metodologia />
    </MainCard>
  );
};